import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  withStyles,
  WithStyles,
  Theme,
  createStyles,
  Box,
  Typography,
} from '@material-ui/core';
import TemplatePromptBar, {
  DEFAULT_TEMPLATE_COUNT,
  PromptSubmission,
} from './TemplatePromptBar';
import TemplateIdeasGallery from './TemplateIdeasGallery';
import GeneratedAdsSection, { AdsNotice } from './GeneratedAdsSection';
import AdsAgentThemeProvider from './AdsAgentThemeProvider';
import { AD } from './adsAgentTokens';
import LeadCaptureDialog, { LeadDialogStep } from './LeadCaptureDialog';
import JoinAiSensyCard from './JoinAiSensyCard';
import PageToast from './PageToast';
import adTemplatesResponse from './adTemplateIdeas';
import {
  getClaimResult,
  getErrorCode,
  getErrorMessage,
  getResult,
  initiate,
  SIGNUP_URL,
  startDraftGeneration,
  startGeneration,
  uploadDraftImage,
  uploadImage,
  verifyOtp,
} from './adsGeneratorApi';
import {
  ClaimResultResponse,
  GeneratedTemplate,
  LeadDetails,
  StoredSession,
} from './adsGenerator.types';
import {
  clearClaim,
  clearSession,
  loadClaim,
  loadSession,
  saveClaim,
  saveSession,
} from './adsSession';

// Public Ads Agent page (ADS-266): prompt → lead + OTP → the Ads Agent job runs
// (rest-api → SQS → py-gen-ai-workers) → the page polls for progress and shows
// the ads in the console cards. The OTP session is kept in the browser until it
// expires (60 min), so a refresh shows the visitor's ads again; after that, a
// new OTP for the same phone shows them.

type PageView = 'form' | 'ads';

// The next poll waits k / (percent + c): POLL_MAX_MS at 0%, shrinking to
// POLL_MIN_MS at 100% (0% → 9s, 30% → 5.6s, 60% → 4.1s, 90% → 3.2s).
const POLL_MIN_MS = 3000;
const POLL_MAX_MS = 9000;
const POLL_OFFSET = (100 * POLL_MIN_MS) / (POLL_MAX_MS - POLL_MIN_MS);

const nextPollDelay = (percent: number): number => {
  const clamped = Math.min(100, Math.max(0, percent));
  return (POLL_MAX_MS * POLL_OFFSET) / (clamped + POLL_OFFSET);
};

const IN_PROGRESS = ['pending', 'processing'];
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyLead: LeadDetails = {
  name: '',
  email: '',
  country: 'India',
  countryCode: '+91',
  phoneNumber: '',
  state: '',
};

// Shown between "generate" and the first result so the skeletons appear at once.
const queuedTemplate = (count: number): GeneratedTemplate => ({
  _id: 'queued',
  status: 'pending',
  templateCount: count,
  progress: { stage: 'Queued', percent: 0 },
  variants: [],
});

const claimTemplate = (result: ClaimResultResponse): GeneratedTemplate => ({
  _id: 'claim',
  status: 'processing',
  templateCount: result.templateCount || DEFAULT_TEMPLATE_COUNT,
  progress: result.progress,
  variants: [],
});

// TEMP: loader preview, remove before pushing. Walks the stages every 2s, then holds on the last one.
const PREVIEW_LOADER = false;
const PREVIEW_STAGES = ['Queued', 'Reading your prompt', 'Choosing the ad types', 'Designing the creative', 'Putting it together'];

const usePreviewTemplate = (): GeneratedTemplate | null => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (!PREVIEW_LOADER || step >= PREVIEW_STAGES.length - 1) return undefined;
    const timer = window.setTimeout(() => setStep(step + 1), 2000);
    return () => window.clearTimeout(timer);
  }, [step]);
  if (!PREVIEW_LOADER) return null;
  return {
    _id: 'preview',
    status: 'processing',
    templateCount: DEFAULT_TEMPLATE_COUNT,
    progress: { stage: PREVIEW_STAGES[step], percent: 0 },
    variants: [],
  };
};

const validateLead = (value: LeadDetails): string => {
  if (!value.name.trim()) {
    return 'Please enter your name.';
  }
  if (!EMAIL_REGEX.test(value.email.trim())) {
    return 'Please enter a valid email address.';
  }
  if (!value.countryCode) {
    return 'Please select your country.';
  }
  if (value.phoneNumber.length < 6 || value.phoneNumber.length > 15) {
    return 'Please enter a valid WhatsApp number.';
  }
  if (!value.state.trim()) {
    return 'Please select your state.';
  }
  return '';
};

const AdsGeneratorPage = (props: Props): React.ReactElement => {
  const { classes } = props;

  const [view, setView] = useState<PageView>('form');
  const previewTemplate = usePreviewTemplate();
  const [submission, setSubmission] = useState<PromptSubmission | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogStep, setDialogStep] = useState<LeadDialogStep>('details');
  const [lead, setLead] = useState<LeadDetails>(emptyLead);
  const [otp, setOtp] = useState('');
  const [phone, setPhone] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [otpChannels, setOtpChannels] = useState<string[]>([]);

  const [template, setTemplate] = useState<GeneratedTemplate | null>(null);
  // This phone's previous generations, newest first, shown under `template`.
  const [earlierTemplates, setEarlierTemplates] = useState<GeneratedTemplate[]>([]);
  const [waMessageSent, setWaMessageSent] = useState(false);
  const [generationError, setGenerationError] = useState('');
  // Set once this phone has used its free generation (until refresh).
  const [limitReached, setLimitReached] = useState(false);
  // True while the visitor's own ads are being loaded (saved session check, or
  // right after OTP), so the static examples don't flash before them.
  const [restoring, setRestoring] = useState(() => loadSession() !== null);

  const pollTimer = useRef<number | null>(null);
  // Bumped on every stop, so a poll still in flight does not schedule the next one.
  const pollRun = useRef(0);
  // Mirrors the session saved in the browser (or the only copy when storage is blocked).
  const sessionRef = useRef<StoredSession | null>(null);
  // Product image id -> uploaded URL, so a retry does not upload twice.
  const uploadedUrls = useRef<Map<string, string>>(new Map());
  // Unverified generation started by this browser (Mode 1 / 2).
  const claimRef = useRef<string | null>(null);
  const draftTokenRef = useRef<string | null>(null);
  const dialogOpenRef = useRef(false);
  const phoneRef = useRef('');
  const lastDraftRejectedRef = useRef(false);
  const [claimNotice, setClaimNotice] = useState<'awaiting' | 'verify' | null>(null);
  const [toast, setToast] = useState('');

  useEffect(() => {
    dialogOpenRef.current = dialogOpen;
  }, [dialogOpen]);

  useEffect(() => {
    phoneRef.current = phone;
  }, [phone]);

  const stopPolling = () => {
    pollRun.current += 1;
    if (pollTimer.current) {
      window.clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  };

  // A failed generation keeps the visitor on the normal page (prompt box and
  // example cards) and shows the message above the cards.
  const fail = (message: string) => {
    stopPolling();
    setGenerationError(message);
    setTemplate(null);
    setView('form');
  };

  const dropClaim = () => {
    claimRef.current = null;
    clearClaim();
    setClaimNotice(null);
  };

  const openVerify = () => {
    setDialogError('');
    setDialogStep(phone ? 'otp' : 'details');
    setDialogOpen(true);
  };

  // `refresh` resolves to the job's percent while it is running, or false once it stopped.
  const pollEvery = useCallback((refresh: () => Promise<number | false>): Promise<void> => {
    stopPolling();
    const run = pollRun.current;
    const tick = (): Promise<void> =>
      refresh().then((percent) => {
        if (percent === false || run !== pollRun.current) {
          return;
        }
        pollTimer.current = window.setTimeout(tick, nextPollDelay(percent));
      });
    return tick();
  }, []);

  // One poll: shows progress/ads and stops once the job is no longer running.
  const refreshResult = useCallback(async (session: StoredSession): Promise<number | false> => {
    try {
      const result = await getResult(session.phone, session.sessionToken);
      if (result.status === 'rejected') {
        fail('');
        setToast(result.message || 'We cannot create ads for this business.');
        return false;
      }
      setWaMessageSent(result.waMessageSent);
      if (result.generationsLeft !== undefined) {
        setLimitReached(result.generationsLeft <= 0);
      }
      if (!result.template) {
        if (result.status === 'failed') {
          fail('We could not generate your ads. Please try again.');
        }
        return false;
      }
      setTemplate(result.template);
      setEarlierTemplates((result.templates || []).slice(1));
      setView('ads');
      const running = IN_PROGRESS.includes(result.template.status);
      if (!running) {
        stopPolling();
        if (result.template.status === 'failed') {
          fail('We could not generate your ads. Please try again.');
        }
      }
      return running ? result.template.progress?.percent || 0 : false;
    } catch (e) {
      if (getErrorCode(e) === 'SESSION_INVALID') {
        clearSession();
        sessionRef.current = null;
      }
      fail(getErrorMessage(e));
      return false;
    }
  }, []);

  const pollResult = useCallback(
    (session: StoredSession) => pollEvery(() => refreshResult(session)),
    [pollEvery, refreshResult],
  );

  const refreshClaim = useCallback(async (claimToken: string, restored = false): Promise<number | false> => {
    try {
      const result = await getClaimResult(claimToken);
      // Verified, replaced or dropped while this request was in flight.
      if (claimRef.current !== claimToken) {
        return false;
      }
      if (result.status === 'rejected') {
        dropClaim();
        lastDraftRejectedRef.current = true;
        setDialogOpen(false);
        fail('');
        setToast(result.message || 'We cannot create ads for this business.');
        return false;
      }
      if (result.status === 'completed') {
        dropClaim();
        const session = sessionRef.current;
        if (session && session.expiresAt > Date.now()) {
          pollResult(session);
        } else {
          fail('');
        }
        return false;
      }
      if (result.status === 'failed') {
        dropClaim();
        fail('We could not generate your ads. Please try again.');
        return false;
      }
      setTemplate(claimTemplate(result));
      setView('ads');
      if (result.status === 'generating') {
        setClaimNotice(null);
        return result.progress?.percent || 0;
      }
      stopPolling();
      setClaimNotice(result.status === 'awaiting_verification' ? 'awaiting' : 'verify');
      if (!dialogOpenRef.current) {
        setDialogStep(phoneRef.current ? 'otp' : 'details');
        setDialogOpen(true);
      }
      return false;
    } catch (e) {
      if (claimRef.current !== claimToken) {
        return false;
      }
      dropClaim();
      // A claim saved on an earlier visit is dropped quietly, like a stale session.
      if (restored) {
        stopPolling();
        setTemplate(null);
        setView('form');
        return false;
      }
      fail(getErrorMessage(e));
      return false;
    }
  }, [pollResult]);

  const pollClaim = useCallback(
    (claimToken: string, restored = false) => pollEvery(() => refreshClaim(claimToken, restored)),
    [pollEvery, refreshClaim],
  );

  useEffect(() => {
    document.title = 'FREE AI Ads Agent by AiSensy';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        "Generate ready-to-run ad creatives for your business in minutes with AiSensy's free AI Ads Agent",
      );
    }
    return stopPolling;
  }, []);

  // Back within the hour: show this visitor's ads again without a new OTP.
  // A session the server no longer accepts is dropped quietly.
  useEffect(() => {
    const saved = loadSession();
    if (!saved) {
      const claim = loadClaim();
      setRestoring(false);
      if (claim) {
        claimRef.current = claim;
        pollClaim(claim, true);
      }
      return;
    }
    clearClaim();
    sessionRef.current = saved;
    setPhone(saved.phone);
    getResult(saved.phone, saved.sessionToken)
      .then((result) => {
        if (result.template) {
          setTemplate(result.template);
          setEarlierTemplates((result.templates || []).slice(1));
          setWaMessageSent(result.waMessageSent);
          if (result.generationsLeft !== undefined) {
            setLimitReached(result.generationsLeft <= 0);
          }
          setView('ads');
          if (IN_PROGRESS.includes(result.template.status)) {
            pollResult(saved);
          }
        }
      })
      .catch(() => {
        clearSession();
        sessionRef.current = null;
        setPhone('');
      })
      .finally(() => setRestoring(false));
  }, [pollResult, pollClaim]);

  const activeSession = (): StoredSession | null => {
    const session = sessionRef.current;
    if (session && session.expiresAt > Date.now()) {
      return session;
    }
    return null;
  };

  const uploadPendingImages = async (
    session: StoredSession,
    images: PromptSubmission['productImages'],
  ): Promise<string[]> => {
    const urls: string[] = [];
    for (const image of images) {
      let url = uploadedUrls.current.get(image.id);
      if (!url) {
        url = await uploadImage(session.phone, session.sessionToken, image.file);
        uploadedUrls.current.set(image.id, url);
      }
      urls.push(url);
    }
    return urls;
  };

  const uploadDraftImages = async (images: PromptSubmission['productImages']): Promise<string[]> => {
    const urls: string[] = [];
    for (const image of images) {
      let url = uploadedUrls.current.get(image.id);
      if (!url) {
        const uploaded = await uploadDraftImage(image.file, draftTokenRef.current);
        draftTokenRef.current = uploaded.draftToken || null;
        url = uploaded.url;
        uploadedUrls.current.set(image.id, url);
      }
      urls.push(url);
    }
    return urls;
  };

  const startDraft = async (current: PromptSubmission) => {
    const sessionAtStart = sessionRef.current;
    dropClaim();
    lastDraftRejectedRef.current = false;
    draftTokenRef.current = null;
    uploadedUrls.current = new Map();
    setGenerationError('');
    setTemplate(queuedTemplate(current.templateCount || DEFAULT_TEMPLATE_COUNT));
    setView('ads');
    try {
      const productImageUrls = await uploadDraftImages(current.productImages);
      const started = await startDraftGeneration({
        prompt: current.prompt,
        aspectRatio: current.aspectRatio,
        adType: current.adType,
        adCount: current.templateCount || DEFAULT_TEMPLATE_COUNT,
        productImageUrls,
        draftToken: draftTokenRef.current || undefined,
      });
      // Verified before this returned: the session poll is already in charge.
      if (started.claimToken && sessionRef.current === sessionAtStart) {
        claimRef.current = started.claimToken;
        saveClaim(started.claimToken);
        pollClaim(started.claimToken);
      }
    } catch (e) {
      setDialogOpen(false);
      fail(getErrorMessage(e));
    }
  };

  // `next` is passed when a new prompt is submitted from an active session, so
  // the backend uses that prompt instead of the one saved at OTP time.
  const startAndPoll = async (session: StoredSession, next?: PromptSubmission) => {
    const current = next || submission;
    setGenerationError('');
    // The ads already on screen move down to "earlier" while the new ones load.
    if (template && template._id !== 'queued') {
      const previous = template;
      setEarlierTemplates((list) => [previous, ...list.filter((t) => t._id !== previous._id)]);
    }
    setTemplate(queuedTemplate(DEFAULT_TEMPLATE_COUNT));
    setView('ads');

    let productImageUrls: string[] = [];
    try {
      productImageUrls = await uploadPendingImages(session, current ? current.productImages : []);
    } catch (e) {
      fail(`We could not upload your images. ${getErrorMessage(e)}`);
      return;
    }

    try {
      await startGeneration(
        session.phone,
        session.sessionToken,
        productImageUrls,
        next ? { prompt: next.prompt, aspectRatio: next.aspectRatio, adType: next.adType } : undefined,
      );
    } catch (e) {
      // This number already used its free generation: show those ads.
      if (getErrorCode(e) !== 'LIMIT_REACHED') {
        fail(getErrorMessage(e));
        return;
      }
      // Limit used up (e.g. in another tab): ask them to sign up, and go back
      // to the ads they already have.
      setLimitReached(true);
      setDialogError('');
      setDialogStep('signup');
      setDialogOpen(true);
    }
    pollResult(session);
  };

  const handlePromptSubmit = (next: PromptSubmission) => {
    setGenerationError('');
    // Saved first: "Use a different number" in the signup popup sends this prompt.
    setSubmission(next);
    if (limitReached) {
      setDialogError('');
      setDialogStep('signup');
      setDialogOpen(true);
      return;
    }
    const session = activeSession();
    if (session) {
      startAndPoll(session, next);
      return;
    }
    setDialogError('');
    setDialogStep('details');
    setDialogOpen(true);
    startDraft(next);
  };

  const sendOtp = async () => {
    // The backend validates the prompt, so a missing one gets its message.
    const response = await initiate({
      name: lead.name.trim(),
      email: lead.email.trim().toLowerCase(),
      phone: lead.countryCode.replace(/\D/g, '') + lead.phoneNumber,
      state: lead.state.trim(),
      prompt: submission ? submission.prompt : '',
      aspectRatio: submission ? submission.aspectRatio : '3:4',
      adType: submission ? submission.adType : 'auto',
      adCount: submission ? submission.templateCount : DEFAULT_TEMPLATE_COUNT,
    });
    if (!claimRef.current) {
      uploadedUrls.current = new Map();
    }
    setPhone(response.phone);
    setOtpChannels(response.otpChannels);
  };

  const handleSendOtp = async () => {
    const error = validateLead(lead);
    setDialogError(error);
    if (error) {
      return;
    }
    setIsSubmitting(true);
    try {
      await sendOtp();
      setOtp('');
      setDialogStep('otp');
    } catch (e) {
      setDialogError(getErrorMessage(e));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    setIsResending(true);
    setDialogError('');
    try {
      await sendOtp();
      setOtp('');
    } catch (e) {
      setDialogError(getErrorMessage(e));
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyOtp = async () => {
    setIsSubmitting(true);
    setDialogError('');
    try {
      const claimToken = claimRef.current;
      const response = await verifyOtp(phone, otp, claimToken || undefined);
      const session: StoredSession = {
        phone,
        sessionToken: response.sessionToken,
        expiresAt: Date.now() + response.expiresInSec * 1000,
      };
      sessionRef.current = session;
      saveSession(session);
      // A number that already generated (or is generating) sees that job;
      // otherwise this starts its free generation. The loader / skeletons are
      // set before the dialog closes, so the static examples never flash.
      if (claimToken) {
        dropClaim();
        const claim = response.claim;
        if (claim && !claim.attached) {
          if (claim.code === 'LIMIT_REACHED') {
            setLimitReached(true);
            setDialogStep('signup');
            setIsSubmitting(false);
            setRestoring(true);
            pollResult(session).finally(() => setRestoring(false));
            return;
          }
          setRestoring(true);
          pollResult(session).finally(() => setRestoring(false));
          if (claim.message) {
            setToast(claim.message);
          }
        } else {
          setRestoring(true);
          pollResult(session).finally(() => setRestoring(false));
        }
      } else if (response.templateId && (response.status === 'completed' || response.status === 'generating')) {
        if (response.generationsLeft <= 0 && response.status === 'completed') {
          setLimitReached(true);
        }
        setRestoring(true);
        pollResult(session).finally(() => setRestoring(false));
      } else if (lastDraftRejectedRef.current) {
        pollResult(session);
      } else {
        startAndPoll(session);
      }
      setDialogOpen(false);
      setIsSubmitting(false);
    } catch (e) {
      setDialogError(getErrorMessage(e));
      setIsSubmitting(false);
    }
  };

  const isGenerating =
    view === 'ads' && !!template && IN_PROGRESS.includes(template.status) && !claimNotice;

  const notice: AdsNotice | undefined = claimNotice
    ? {
        title:
          claimNotice === 'awaiting'
            ? 'Verify your number to continue generating your ads'
            : 'Please verify first',
        text:
          claimNotice === 'awaiting'
            ? 'We have understood your business. Verify your number and we will finish your ads.'
            : 'Your ads are ready. Verify your number to see them.',
        actionLabel: 'Verify now',
        onAction: openVerify,
      }
    : undefined;

  return (
    <div className={classes.root}>
      <div className={classes.body}>
        <div className={classes.content}>
          <AdsAgentThemeProvider>
            <div className={classes.panel}>
              <TemplatePromptBar
                submitting={isGenerating}
                disabled={dialogOpen || isGenerating}
                onSubmit={handlePromptSubmit}
              />
            </div>

            {generationError && view === 'form' ? (
              <Box mb={2}>
                <Typography variant="body2" color="error" role="alert">
                  {generationError}
                </Typography>
              </Box>
            ) : null}

            {restoring && view === 'form' ? (
              <GeneratedAdsSection
                template={queuedTemplate(DEFAULT_TEMPLATE_COUNT)}
                waMessageSent={false}
                signupUrl={SIGNUP_URL}
              />
            ) : null}

            {previewTemplate ? (
              <GeneratedAdsSection template={previewTemplate} waMessageSent={false} signupUrl={SIGNUP_URL} />
            ) : null}

            {view === 'form' && !restoring && !previewTemplate ? (
              <TemplateIdeasGallery templates={adTemplatesResponse.data} />
            ) : null}

            {view === 'ads' && limitReached ? <JoinAiSensyCard signupUrl={SIGNUP_URL} /> : null}

            {view === 'ads' && template ? (
              <GeneratedAdsSection
                template={template}
                earlierTemplates={earlierTemplates}
                waMessageSent={waMessageSent}
                signupUrl={SIGNUP_URL}
                notice={notice}
              />
            ) : null}

          </AdsAgentThemeProvider>

        </div>
      </div>

      <LeadCaptureDialog
        open={dialogOpen}
        step={dialogStep}
        details={lead}
        onDetailsChange={setLead}
        onSendOtp={handleSendOtp}
        otp={otp}
        onOtpChange={setOtp}
        onVerifyOtp={handleVerifyOtp}
        onResendOtp={handleResendOtp}
        onEditDetails={() => {
          // The limit belonged to the previous number.
          if (dialogStep === 'signup') {
            setLimitReached(false);
          }
          setDialogError('');
          setDialogStep('details');
        }}
        onClose={() => setDialogOpen(false)}
        isSubmitting={isSubmitting}
        isResending={isResending}
        error={dialogError}
        signupUrl={SIGNUP_URL}
        otpChannels={otpChannels}
        verifiedPhone={phone}
      />
      <PageToast message={toast} onClose={() => setToast('')} />
    </div>
  );
};

interface Props extends WithStyles<typeof styles> {}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      minHeight: '100%',
      backgroundColor: AD.surfaceSunken,
      boxSizing: 'border-box',
    },
    body: {
      padding: '24px',
      boxSizing: 'border-box',
      [theme.breakpoints.down('xs')]: { padding: '16px 12px' },
    },
    content: {
      maxWidth: 1400,
      margin: '0 auto',
    },
    panel: {
      borderRadius: AD.radiusLg,
      overflow: 'hidden',
      marginBottom: 24,
    },
    heading: {
      fontWeight: 600,
    },
    whiteText: {
      color: theme.palette.common.white,
    },
  });

export default withStyles(styles)(AdsGeneratorPage);
